/**
 * UpdateGalleryLinkModal.tsx - Gallery Link Management Modal
 *
 * This component provides an interface for managing and updating gallery
 * links including custom URLs and sharing configurations.
 *
 * Key Features:
 * - Custom gallery URL management
 * - Link validation and availability checking
 * - URL format validation and suggestions
 * - Link copying and sharing functionality
 * - Domain and path customization
 * - Link preview and testing
 *
 * Link Management:
 * - Base gallery URL customization
 * - Path and subdomain options
 * - URL format validation
 * - Link availability checking
 * - Preview and testing functionality
 *
 * Props:
 * - isVisible: boolean - Modal visibility state
 * - onClose: () => void - Close modal handler
 * - onLinkUpdated: (link: string) => void - Link update handler
 *
 * Dependencies: URL validation, clipboard API, portal rendering
 */

import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { X } from "lucide-react";
import { GallerySettingsModel } from "../models/GalleryModels";
import { AuthService } from "@mindmemobile/mm-shared-global";
import { FundraiserService } from "../services";
import { firstValueFrom } from "rxjs";
import { fundraiserStore } from "../stores";
import ToastService from "./ToastService";

const ModalOverlay = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div<{ hasWarning: boolean }>`
  background: #ffffff;
  border-radius: 24px;
  padding: 40px;
  width: 90%;
  max-width: 500px;
  max-height: ${(props) => (props.hasWarning ? "650px" : "550px")};
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  flex: 1;
  text-align: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s ease;

  &:hover {
    background: #e2e8f0;
    transform: scale(1.05);
  }
`;

const ModalText = styled.p`
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 24px;
  font-size: 16px;
  &.modal-text-margin {
    margin-top: 30px;
  }
`;

const URLInputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;

const DomainSelect = styled.select`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  background: #f8fafc;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const NameInputContainer = styled.div`
  position: relative;
  flex: 1;
  width: 100%;
`;

const NameInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px 16px 12px 114px;
  border: 2px solid ${(props) => (props.hasError ? "#dc2626" : "#e2e8f0")};
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? "#dc2626" : "#3b82f6")};
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.hasError ? "rgba(220, 38, 38, 0.1)" : "rgba(59, 130, 246, 0.1)"};
  }
`;

const URLPrefix = styled.span`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  color: #64748b;
  font-weight: 500;
  pointer-events: none;
`;

const URLPreview = styled.div<{ hasError?: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  padding: 8px 0;
  margin-top: 10px;
  font-size: 12px;
  text-align: left;
  margin-bottom: 20px;
  font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
  color: ${(props) => (props.hasError ? "#dc2626" : "#64748b")};
  word-break: break-all;
  z-index: 10;
`;

const YellowWarningText = styled.div`
  background: #fef2f2;
  border: 1px solid #dc2626;
  border-radius: 12px;
  padding: 16px;
  margin: 60px 0 32px 0;
  color: #dc2626;
  font-weight: 600;
  text-align: center;
  font-size: 16px;
  line-height: 1.4;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 24px;
`;

const ModalButton = styled.button<{ variant?: "danger" }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${(props) =>
    props.variant === "danger"
      ? `
    background: #dc2626;
    color: #ffffff;

    &:hover {
      background: #b91c1c;
    }

    &:disabled {
      background: #9ca3af;
      cursor: not-allowed;
    }
  `
      : `
    background: #f1f5f9;
    color: #64748b;

    &:hover {
      background: #e2e8f0;
    }
  `}
`;

interface UpdateGalleryLinkModalProps {
  isVisible: boolean;
  onClose: () => void;
  gallerySettings?: GallerySettingsModel;
  onLinkCopied: (message: string) => void;
}

export const UpdateGalleryLinkModal: React.FC<UpdateGalleryLinkModalProps> = ({
  isVisible,
  onClose,
  gallerySettings,
  onLinkCopied,
}) => {
  const domain = new URL(fundraiserStore.getFundRaiserDomainBaseURL()).hostname;
  const [selectedDomain, setSelectedDomain] = useState(domain);
  const [originalDomain, setOriginalDomain] = useState(domain);
  const [galleryName, setGalleryName] = useState(
    `${gallerySettings?.pageName || ""}`
  );
  const [hasChanges, setHasChanges] = useState(false);
  const [urlError, setUrlError] = useState(false);

  useEffect(() => {
    if (isVisible) {
      // const randomString = Math.random().toString(36).substring(2, 12);
      // if (!gallerySettings?.pageName) {
      //   setGalleryName(`fundraisers/gallery/${randomString}`);
      // }
      setSelectedDomain(`${domain}`);
      setOriginalDomain(`${domain}`);
      setHasChanges(false);
      setUrlError(false);
    }
  }, [isVisible]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  useEffect(() => {
    if (isVisible) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [isVisible]);

  const validateUrl = (name: string) => {
    const invalidChars = /[^a-zA-Z0-9\-\.&?]/;
    return !invalidChars.test(name);
  };

  const handleNameChange = (value: string) => {
    setGalleryName(value);
    const isValid = validateUrl(value);
    setUrlError(!isValid);
    setHasChanges(
      value !== gallerySettings?.pageName || selectedDomain !== originalDomain
    );
  };

  const handleDomainChange = (value: string) => {
    setSelectedDomain(value);
    setHasChanges(
      value !== originalDomain || galleryName !== gallerySettings?.pageName
    );
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
  };

  const fullUrl = fundraiserStore.getPublicGalleryLinkByName(
    AuthService.shortUniqueId,
    galleryName,
    fundraiserStore.selectedPromoterId || ""
  );
  const handleCopyLinkToClipboard = () => {
    copyToClipboard(fullUrl);
    onLinkCopied("Gallery link copied to clipboard");
  };

  const handleUpdateAndCopy = async () => {
    if (!urlError) {
      try {
        const requestModel = {
          id: gallerySettings?.id || "",
          url: galleryName,
          accountId: AuthService.authData.accountId || "",
          userId: AuthService.mindmeAccountDetails?.userId || "",
        };

        const response = await firstValueFrom(
          FundraiserService.updateGalleryURL(requestModel)
        );
        if (response.result.success) {
          onClose();
          copyToClipboard(fullUrl);
          onLinkCopied("Gallery link updated and copied to clipboard");
        } else if (response.result.failureReason === "AlreadyExists") {
          ToastService.showError("Gallery link already exists");
        }
      } catch (error) {
        console.error("Error saving gallery link to localStorage:", error);
      }
    }
  };

  const shouldShowWarning = hasChanges;

  if (!isVisible) return null;

  return createPortal(
    <ModalOverlay visible={isVisible} onClick={handleOverlayClick}>
      <ModalContent hasWarning={shouldShowWarning}>
        <ModalHeader>
          <ModalTitle>Update Gallery Link</ModalTitle>
        </ModalHeader>

        <CloseButton onClick={onClose}>
          <X size={20} />
        </CloseButton>

        <ModalText className="modal-text-margin">
          This is a general link for the gallery.
        </ModalText>

        <URLInputsContainer>
          <DomainSelect
            value={selectedDomain}
            onChange={(e) => handleDomainChange(e.target.value)}
          >
            <option value={`${domain}`}>{domain}/</option>
            {/* <option value="myfundraiser.co/">myfundraiser.co/</option>
            <option value="supportmycause.org/">donatenow.us/</option>
            <option value="donatenow.us/">supportmycause.org/</option> */}
          </DomainSelect>

          <NameInputContainer>
            <URLPrefix>gallery/{AuthService.shortUniqueId}/</URLPrefix>
            <NameInput
              type="text"
              value={galleryName} // Show only the part after "gallery/"
              onChange={(e) => handleNameChange(`${e.target.value}`)}
              hasError={urlError}
              placeholder="gallery-name"
            />
            <URLPreview hasError={urlError}>{fullUrl}</URLPreview>
          </NameInputContainer>
        </URLInputsContainer>

        {shouldShowWarning && (
          <YellowWarningText>
            Changing Gallery URL will cause previous gallery link to stop
            working.
          </YellowWarningText>
        )}

        <ModalActions
          style={{
            marginTop: shouldShowWarning ? "24px" : "64px",
            justifyContent: "center",
          }}
        >
          {!hasChanges ? (
            <>
              <ModalButton onClick={onClose}>Close</ModalButton>
              <ModalButton
                onClick={handleCopyLinkToClipboard}
                style={{
                  background: "#3b82f6",
                  color: "#ffffff",
                }}
              >
                Copy
              </ModalButton>
            </>
          ) : (
            <>
              <ModalButton onClick={onClose}>Cancel</ModalButton>
              <ModalButton
                onClick={handleUpdateAndCopy}
                disabled={urlError}
                style={{
                  background: urlError ? "#9ca3af" : "#dc2626",
                  color: "#ffffff",
                  cursor: urlError ? "not-allowed" : "pointer",
                }}
              >
                Update & Copy
              </ModalButton>
            </>
          )}
        </ModalActions>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};
